import React, { useEffect, useState } from "react";
import { Card, Form, Button, Row, Col } from "@themesberg/react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { addTranches, getContact, updateTranche } from "../../service/api";
import { showNotification } from "../../service/showNotification";
import { trancheSchema } from "../../yup";
import { tracker } from "../../service/mixpanel";
import useDebounce from "../../hooks/useDebounce";
import TrancheConfirmationModal from "./TrancheConfirmationModal";
import ConfirmationModal from "../../helper/ConfirmationModal";

const TranchesForm = ({ tranche, tranches, onClose, getTranches }) => {
  const isEditMode = !!tranche;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(trancheSchema),
    defaultValues: {
      trancheName: tranche?.trancheName || "",
      clientName: tranche?.clientName || "",
      groupType: tranche?.groupType || "",
      entityName: tranche?.entityName || "",
      entityFullAddress: tranche?.entityFullAddress || "",
      trancheContactId: tranche?.trancheContactId || "",
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const contactId = watch("trancheContactId");
  const debouncedId = useDebounce(contactId, 1000);

  const resetContactFields = () => {
    setValue("clientName", "");
    setValue("groupType", "");
    setValue("entityName", "");
    setValue("entityFullAddress", "");
  };

  const fetchContact = async () => {
    if (!debouncedId) {
      setIsLoading(false);
      resetContactFields();
      return;
    }
    try {
      setIsLoading(true);
      const res = await getContact(debouncedId);
      const contact = res?.data;
      if (!contact) throw new Error("Contact not found");

      const clientName = `${contact?.firstName || ""} ${
        contact?.lastName || ""
      }`.trim();

      setValue("clientName", clientName || "");
      setValue("groupType", contact?.contactGroupType || "");
      setValue("entityName", contact?.entity?.name || "");
      setValue("entityFullAddress", contact?.entity?.fullAddress || "");
    } catch (error) {
      console.log("contact not found", error);
      showNotification("error", "Contact not found or failed to fetch details");
      resetContactFields();
    } finally {
      setIsLoading(false);
    }
  };

  const processSubmit = async (data) => {
    tracker.tranches.trancheFormSubmitted(tranche?._id, isEditMode);
    try {
      if (isEditMode) {
        await updateTranche(tranche._id, data);
        showNotification("success", "Tranche updated successfully!");
      } else {
        await addTranches(data);
        showNotification("success", "Tranche added successfully!");
        reset();
      }
      getTranches();
      onClose();
    } catch (error) {
      showNotification(
        "error",
        `Failed to ${isEditMode ? "update" : "add"} tranche. Please try again.`
      );
    }
  };

  const onSubmit = async (data) => {
    if (!isEditMode && !data.trancheContactId) {
      setShowConfirmModal(true);
    } else {
      await processSubmit(data);
    }
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    handleSubmit(processSubmit)();
  };

  // common input field
  const renderField = (name, label, required = true, isDisabled = false) => (
    <Form.Group controlId={name} className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        type="text"
        // placeholder={`Enter ${label}`}
        {...register(name)}
        isInvalid={!!errors[name]}
        disabled={isSubmitting || isDisabled}
      />
      {required && (
        <Form.Control.Feedback type="invalid">
          {errors[name]?.message}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );

  useEffect(() => {
    fetchContact();
  }, [debouncedId]);

  const isSubmitButtonDisabled =
    isSubmitting || isLoading || contactId !== debouncedId;

  return (
    <Card border="light" className="bg-white shadow-sm mb-4">
      <Card.Body>
        <h5 className="mb-4">{isEditMode ? "Edit Tranche" : "Add Tranche"}</h5>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col md={6}>{renderField("trancheName", "Tranche Name", true)}</Col>
            <Col md={6}>
              {renderField("trancheContactId", "Contact ID", false)}
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              {renderField("clientName", "Client Name", false, true)}
            </Col>
            <Col md={6}>
              {renderField("groupType", "Group Type", false, true)}
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              {renderField("entityName", "Entity Name", false, true)}
            </Col>
            <Col md={6}>
              {renderField(
                "entityFullAddress",
                "Entity Full Address",
                false,
                true
              )}
            </Col>
          </Row>
          <div className="mt-3">
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitButtonDisabled}
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                ? "Update Tranche"
                : "Add Tranche"}
            </Button>
          </div>
        </Form>
      </Card.Body>

      <ConfirmationModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleConfirm}
        title="Confirmation"
        message={null}
      >
        <TrancheConfirmationModal />
      </ConfirmationModal>
    </Card>
  );
};

export default TranchesForm;
